/**
 * RescheduleRouteModal — verplaats een complete route (met alle stops) naar
 * een andere dag.
 *
 * Server-side (POST /routes/{id}/reschedule):
 *   - route-datum + alle WO-afspraken schuiven mee (tijdvenster behouden)
 *   - chauffeur wordt losgekoppeld → route wordt open dienst op de nieuwe dag
 *   - klanten krijgen de S03B/S08F "afspraak gewijzigd"-mail met wijzig-link
 *
 * Gedeeld tussen de route-planner (toolbar) en /rooster (AssignRouteDialog),
 * daarom gevoed met kale props i.p.v. een specifieke store.
 */

import { useState } from 'react';
import { useCalendarStore } from '../../stores/calendar-store';
import type { RescheduleRouteResult } from '../../lib/salesforce/types';

interface RescheduleRouteModalProps {
  routeId: string;
  routeName: string;
  /** YYYY-MM-DD — huidige datum van de route */
  oldDate: string;
  chauffeurName?: string | null;
  ophalenCount: number;
  terugbrengenCount: number;
  locked: boolean;
  onClose: () => void;
  /** Na een geslaagde verplaatsing (modal blijft open met succes-samenvatting) */
  onSuccess?: (result: RescheduleRouteResult) => void;
}

const ERROR_MESSAGES: Record<string, string> = {
  ROUTE_LOCKED: 'Deze route is vergrendeld — ontgrendel hem eerst in de planner.',
  ROUTE_HAS_PROCESSED_STOPS:
    'Deze route heeft al verwerkte stops (deels gereden) en kan niet verplaatst worden.',
  ROUTE_CANCELLED: 'Deze route is geannuleerd.',
  ROUTE_NOT_FOUND: 'Route niet gevonden — ververs de pagina.',
  INVALID_DATE: 'Ongeldige datum — kies een dag vanaf vandaag.',
  SAME_DATE: 'De route staat al op deze datum.',
};

function formatDateNl(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function isTomorrow(dateStr: string): boolean {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const d = new Date(dateStr + 'T00:00:00');
  return d.toDateString() === tomorrow.toDateString();
}

export function RescheduleRouteModal({
  routeId,
  routeName,
  oldDate,
  chauffeurName,
  ophalenCount,
  terugbrengenCount,
  locked,
  onClose,
  onSuccess,
}: RescheduleRouteModalProps) {
  const { rescheduleRoute, isRescheduling } = useCalendarStore();

  const [newDate, setNewDate] = useState('');
  const [notifyCustomers, setNotifyCustomers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    (RescheduleRouteResult & { scheduleCleanupFailed: boolean }) | null
  >(null);

  const todayStr = new Date().toISOString().slice(0, 10);
  const stopsTotal = ophalenCount + terugbrengenCount;
  const canSubmit = !!newDate && newDate !== oldDate && !isRescheduling && !locked;

  // Dag-vóór-tijdenmail (15:00) waarschuwing: relevant als de oude dag morgen
  // is (klanten hebben mogelijk al definitieve tijden) of als de nieuwe dag
  // morgen is en de 15:00-run al geweest is (geen tijdenmail meer).
  const showTijdenmailWarning =
    isTomorrow(oldDate) || (!!newDate && isTomorrow(newDate) && new Date().getHours() >= 15);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      const res = await rescheduleRoute(routeId, oldDate, newDate, notifyCustomers);
      setResult(res);
      onSuccess?.(res);
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      const mapped = code ? ERROR_MESSAGES[code] : undefined;
      setError(
        mapped || (err instanceof Error ? err.message : 'Route verplaatsen mislukt')
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Verplaats naar andere dag: {routeName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-gray-400 hover:text-gray-600"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4 capitalize">{formatDateNl(oldDate)}</p>

        {result ? (
          // Succes-samenvatting
          <>
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 leading-relaxed">
              Route verplaatst naar <strong>{formatDateNl(result.newDate)}</strong>.{' '}
              {result.mailsQueued > 0
                ? `${result.mailsQueued} klant${result.mailsQueued !== 1 ? 'en' : ''} ${
                    result.mailsQueued !== 1 ? 'ontvangen' : 'ontvangt'
                  } een e-mail met het nieuwe moment en een wijzig-link.`
                : 'Klanten zijn niet gemaild.'}
              {result.unassignedChauffeurId && (
                <>
                  {' '}
                  De route staat als <strong>open dienst</strong> klaar voor toewijzing in het
                  rooster.
                </>
              )}
            </div>
            {result.scheduleCleanupFailed && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                De oude rooster-dienst kon niet automatisch verwijderd worden — verwijder deze
                handmatig op het rooster van {formatDateNl(oldDate)}.
              </div>
            )}
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Sluiten
              </button>
            </div>
          </>
        ) : (
          <>
            {locked && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Deze route is vergrendeld — ontgrendel hem eerst in de planner.
              </div>
            )}

            {/* Datumkiezer */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                Nieuwe datum
              </label>
              <input
                type="date"
                value={newDate}
                min={todayStr}
                onChange={(e) => setNewDate(e.target.value)}
                disabled={locked}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50"
              />
            </div>

            {/* Samenvatting */}
            {newDate && newDate !== oldDate && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                <strong>
                  {ophalenCount} ophaal-stop{ophalenCount !== 1 ? 's' : ''} en {terugbrengenCount}{' '}
                  aflever-stop{terugbrengenCount !== 1 ? 's' : ''}
                </strong>{' '}
                verhuizen van <strong className="capitalize">{formatDateNl(oldDate)}</strong> naar{' '}
                <strong className="capitalize">{formatDateNl(newDate)}</strong>. Tijdvensters
                blijven behouden.
              </div>
            )}
            {newDate && newDate === oldDate && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                De route staat al op deze datum — kies een andere dag.
              </div>
            )}

            {/* Klanten informeren */}
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyCustomers}
                onChange={(e) => setNotifyCustomers(e.target.checked)}
                disabled={locked}
                className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-900">
                  Klanten informeren per e-mail (aanbevolen)
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Elke klant ontvangt een mail met het nieuwe moment en een link om de afspraak
                  zelf te wijzigen.
                </span>
              </span>
            </label>

            {/* Waarschuwingen */}
            {chauffeurName && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>{chauffeurName}</strong> wordt losgekoppeld — de route komt als open
                dienst op de nieuwe dag en moet opnieuw toegewezen worden.
              </div>
            )}
            {showTijdenmailWarning && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                De dag-vóór-tijdenmail (15:00) is mogelijk al verstuurd of wordt overgeslagen —
                controleer of klanten de juiste tijden hebben.
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end mt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isRescheduling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRescheduling ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verplaatsen…
                  </>
                ) : (
                  `Route verplaatsen${stopsTotal > 0 ? ` (${stopsTotal} stops)` : ''}`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
